/**
 * DailyViewChart
 *
 * Overlaid line chart showing 7-day moving average billable hours for two periods:
 * - After (Hourglass period): each day from the attorney's go-live date to today
 * - Before (prior year): the same calendar dates shifted back 1 year
 *
 * Raw daily lines are hidden — only the smoothed moving average lines are shown.
 */

import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DailyViewData } from "../../types/dashboard";
import { DAILY_CHART_COLORS } from "../../config/dashboardTheme";

// Series names — referenced in both <Line name> props and tooltip lookup
const SERIES = {
  afterMA: "After 7-day Moving Average",
  beforeMA: "Before 7-day Moving Average",
} as const;

interface ChartPoint {
  label: string;      // "Feb 12" — shown on X axis
  afterDate: string;  // "2025-02-12"
  beforeDate: string; // "2024-02-12"
  afterHours: number;
  beforeHours: number;
  afterMA: number | null;
  beforeMA: number | null;
}

/** Timezone-safe YYYY-MM-DD string from a local Date (avoids toISOString UTC offset). */
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** O(n) trailing moving average using a sliding window sum. Returns null for the first windowSize-1 points. */
function trailingMA(values: number[], windowSize: number): (number | null)[] {
  const result: (number | null)[] = [];
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= windowSize) sum -= values[i - windowSize];
    result.push(i < windowSize - 1 ? null : Math.round((sum / windowSize) * 10) / 10);
  }
  return result;
}

function buildChartData(data: DailyViewData): ChartPoint[] {
  if (data.after.length === 0) return [];

  const afterMap = new Map(data.after.map((d) => [d.date, d.hours]));
  const beforeMap = new Map(data.before.map((d) => [d.date, d.hours]));

  // Generate every calendar day in the after window for a continuous time axis
  const firstDate = new Date(data.after[0].date + "T00:00:00");
  const lastDate = new Date(data.after[data.after.length - 1].date + "T00:00:00");

  const points: Omit<ChartPoint, "afterMA" | "beforeMA">[] = [];
  const cur = new Date(firstDate);

  while (cur <= lastDate) {
    const afterDate = toLocalDateStr(cur);

    const beforeDateObj = new Date(cur);
    beforeDateObj.setFullYear(beforeDateObj.getFullYear() - 1);
    const beforeDate = toLocalDateStr(beforeDateObj);

    points.push({
      label: formatDateLabel(afterDate),
      afterDate,
      beforeDate,
      afterHours: afterMap.get(afterDate) ?? 0,
      beforeHours: beforeMap.get(beforeDate) ?? 0,
    });

    cur.setDate(cur.getDate() + 1);
  }

  const afterMA = trailingMA(points.map((p) => p.afterHours), 7);
  const beforeMA = trailingMA(points.map((p) => p.beforeHours), 7);

  return points.map((p, i) => ({ ...p, afterMA: afterMA[i], beforeMA: beforeMA[i] }));
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name: string; value: number | null; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  const afterMA = payload.find((p) => p.name === SERIES.afterMA);
  const beforeMA = payload.find((p) => p.name === SERIES.beforeMA);

  return (
    <div
      style={{
        backgroundColor: "var(--popover)",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        padding: "10px 14px",
        fontSize: "13px",
      }}
    >
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {afterMA?.value != null && (
        <p style={{ color: DAILY_CHART_COLORS.afterMA }}>
          After 7d avg: {afterMA.value} hrs
        </p>
      )}
      {beforeMA?.value != null && (
        <p style={{ color: DAILY_CHART_COLORS.beforeMA }}>
          Before 7d avg: {beforeMA.value} hrs
        </p>
      )}
    </div>
  );
}

export default function DailyViewChart({ data }: { data: DailyViewData }) {
  const chartData = useMemo(() => buildChartData(data), [data]);

  if (chartData.length === 0) {
    return (
      <p className="text-center text-muted-foreground text-sm py-12">
        No daily data available for this attorney.
      </p>
    );
  }

  // Show ~8 X-axis labels regardless of data length
  const xInterval = Math.max(0, Math.floor(chartData.length / 8) - 1);

  return (
    <ResponsiveContainer width="100%" height={380}>
      <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11 }}
          stroke="var(--muted-foreground)"
          interval={xInterval}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="var(--muted-foreground)"
          tickFormatter={(v) => `${v}h`}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          formatter={(value) => (
            <span style={{ fontSize: 12, color: "var(--foreground)" }}>{value}</span>
          )}
        />

        {/* 7-day moving average lines with dots at each point */}
        <Line
          type="monotone"
          dataKey="afterMA"
          name={SERIES.afterMA}
          stroke={DAILY_CHART_COLORS.afterMA}
          strokeWidth={2}
          dot={{ fill: DAILY_CHART_COLORS.afterMA, r: 3, strokeWidth: 0 }}
          activeDot={{ fill: DAILY_CHART_COLORS.afterMA, r: 5 }}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="beforeMA"
          name={SERIES.beforeMA}
          stroke={DAILY_CHART_COLORS.beforeMA}
          strokeWidth={2}
          dot={{ fill: DAILY_CHART_COLORS.beforeMA, r: 3, strokeWidth: 0 }}
          activeDot={{ fill: DAILY_CHART_COLORS.beforeMA, r: 5 }}
          connectNulls={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
